import {z} from 'zod';

import {BaseVoidResponseSchema} from '@dms/api/shared';

const WarehousePricingEnumSchema = z.enum(['AVG', 'FIFO']);
export type WarehousePricingEnum = z.infer<typeof WarehousePricingEnumSchema>;

export const SalesPriceCalculationEnumSchema = z.enum([
  'MARGIN_ON_BASE',
  'MARKUP_ON_BASE',
  'PROFIT_ON_BASE',
  'MANUAL',
  'RECOMMENDED_PRICE',
  'MARKETING_CODE',
]);
export type SalesPriceCalculationEnum = z.infer<typeof SalesPriceCalculationEnumSchema>;

export const BasePriceSourceEnumSchema = z.enum([
  'AVG_PURCHASE',
  'LAST_PURCHASE',
  'RECOMMENDED',
  'MANUAL',
  'PURCHASE_PRICE_DEF_SUPPLIER',
]);
export type BasePriceSourceEnum = z.infer<typeof BasePriceSourceEnumSchema>;

const WarehouseSupplierSchema = z.object({
  id: z.string().nullable(),
});

export const WarehouseSpaceSchema = z.object({
  spaceId: z.string(),
  spaceName: z.string(),
});
export type WarehouseSpace = z.infer<typeof WarehouseSpaceSchema>;

export const GetWarehouseResponseSchema = z.object({
  created: z.string(),
  createdBy: z.string(),
  updated: z.string().nullish(),
  updatedBy: z.string().nullish(),
  id: z.string(),
  name: z.string(),
  warehouseNumber: z.string(),
  address: z.string().nullish(),
  supportedMake: z.string().nullish(),
  defaultSupplierId: z.string(),
  warehousePricing: WarehousePricingEnumSchema,
  defaultOrderShipping: z.string().nullish(),
  branchId: z.string(),
  companyBillingInfoId: z.string(),
  allowServiceOrderIssue: z.boolean(),
  allowedOrderVariants: z.array(z.string()).nullish(),
  commercialOrderMarginMinimum: z.number().nullish(),
  stockNumberPrefix: z.string().nullish(),
  receiveNoteSeriesId: z.string(),
  receiveNoteTemplateId: z.string().nullish(),
  issueNoteSeriesId: z.string(),
  defaultManufacturerId: z.string().nullish(),
  issueNoteTemplateId: z.string().nullish(),
  salesPriceCalculation: SalesPriceCalculationEnumSchema,
  basePriceSource: BasePriceSourceEnumSchema,
  salePriceProfitWithoutVat: z.number().nullish(),
  salePriceMarginPercent: z.number().nullish(),
  salePriceMarkupPercent: z.number().nullish(),
  blockPricingSettingOnArticle: z.boolean(),
  unitSalesPriceWithVat: z.boolean(),
  isFastTrack: z.boolean(),
  dummyWarehouse: z.boolean(),
  restrictSaleBelowCost: z.boolean().optional(),
  allSuppliers: z.boolean(),
  suppliers: z.array(WarehouseSupplierSchema),
  allowDirectSaleIssue: z.boolean(),
  allowedDirectSaleVariants: z.array(z.string()).nullish(),
  authorizationProfileId: z.string(),
  currency: z.string(),
  creditNoteSeriesId: z.string(),
  creditNoteTemplateId: z.string().nullish(),
  issueNoteCorrectionSeriesId: z.string(),
  issueNoteCorrectionTemplateId: z.string().nullish(),
  workItemGroupId: z.string(),
  spaces: z.array(WarehouseSpaceSchema).nullish(),
});

export const WarehouseDetailSchema = z.object({
  name: z.string(),
  warehouseNumber: z.string().nullish(),
  address: z.string().nullish(),
  supportedMake: z.string().nullish(),
  defaultManufacturerId: z.string().nullish(),
  defaultSupplierId: z.string(),
  warehousePricing: WarehousePricingEnumSchema,
  defaultOrderShipping: z.string().nullish(),
  branchId: z.string(),
  companyBillingInfoId: z.string(),
  allowServiceOrderIssue: z.boolean(),
  allowedOrderVariants: z.array(z.string()).nullish(),
  commercialOrderMarginMinimum: z.number().nullish(),
  stockNumberPrefix: z.string().nullish(),
  receiveNoteSeriesId: z.string(),
  receiveNoteTemplateId: z.string().nullish(),
  issueNoteSeriesId: z.string(),
  issueNoteTemplateId: z.string().nullish(),
  salesPriceCalculation: SalesPriceCalculationEnumSchema,
  basePriceSource: BasePriceSourceEnumSchema,
  salePriceProfitWithoutVat: z.number().nullish(),
  salePriceMarginPercent: z.number().nullish(),
  salePriceMarkupPercent: z.number().nullish(),
  blockPricingSettingOnArticle: z.boolean(),
  unitSalesPriceWithVat: z.boolean(),
  isFastTrack: z.boolean(),
  dummyWarehouse: z.boolean(),
  restrictSaleBelowCost: z.boolean().optional(),
  allSuppliers: z.boolean(),
  suppliers: z.array(WarehouseSupplierSchema),
  allowDirectSaleIssue: z.boolean(),
  allowedDirectSaleVariants: z.array(z.string()).nullish(),
  authorizationProfileId: z.string(),
  currency: z.string(),
  creditNoteSeriesId: z.string().nullish(),
  creditNoteTemplateId: z.string().nullish(),
  issueNoteCorrectionSeriesId: z.string().nullish(),
  issueNoteCorrectionTemplateId: z.string().nullish(),
  workItemGroupId: z.string().nullish(),
});

/**
 * GET
 */
export const GetWarehousesRequestSchema = z.union([
  z.object({
    serviceOrderVariantId: z.string().optional(),
    directSaleVariantId: z.string().optional(),
    authorizationProfileId: z.string().optional(),
    supplierId: z.string().optional(),
    branchId: z.string().optional(),
    // Opts this call into the route version that checks the warehouse ACL scopes
    isAclScoped: z.boolean().optional(),
  }),
  z.void(),
]);
export type GeWarehousesRequest = z.infer<typeof GetWarehousesRequestSchema>;

export const GetWarehousesResponseSchema = z.array(
  z.object({
    id: z.string(),
    warehouseNumber: z.string().optional(),
    name: z.string(),
    authorizationProfileId: z.string(),
    defaultManufacturerId: z.string().optional(),
  })
);
export type GetWarehousesResponse = z.infer<typeof GetWarehousesResponseSchema>;

export const GetWarehouseRequestSchema = z.object({
  warehouseId: z.string(),
  // Opts this call into the route version that checks the warehouse ACL scopes
  isAclScoped: z.boolean().optional(),
});
export type GetWarehouseRequest = z.infer<typeof GetWarehouseRequestSchema>;

export type GetWarehouseResponse = z.infer<typeof GetWarehouseResponseSchema>;

/**
 * POST
 */
export const PostWarehouseRequestSchema = WarehouseDetailSchema.omit({currency: true});
export type PostWarehouseRequest = z.infer<typeof PostWarehouseRequestSchema>;

export const PostWarehouseResponseSchema = z.object({
  warehouseId: z.string(),
});
export type PostWarehouseResponse = z.infer<typeof PostWarehouseResponseSchema>;

/**
 * PATCH
 */
export const PatchWarehouseRequestSchema = z.object({
  warehouseId: z.string(),
  // spaceIds is only supported by the v4 PATCH endpoint, the POST endpoint does not accept it
  body: WarehouseDetailSchema.omit({currency: true}).extend({
    spaceIds: z.array(z.string()).nullish(),
  }),
});
export type PatchWarehouseRequest = z.infer<typeof PatchWarehouseRequestSchema>;

export const PatchWarehouseResponseSchema = BaseVoidResponseSchema;
export type PatchWarehouseResponse = z.infer<typeof PatchWarehouseResponseSchema>;

/**
 * DELETE
 */
export const DeleteWarehouseRequestSchema = z.object({
  body: z.object({
    warehouseId: z.array(z.string()),
  }),
});
export type DeleteWarehouseRequest = z.infer<typeof DeleteWarehouseRequestSchema>;

export const DeleteWarehouseResponseSchema = BaseVoidResponseSchema;
export type DeleteWarehouseResponse = z.infer<typeof DeleteWarehouseResponseSchema>;
